import { SVSummaryCard } from '@/components/core';
import ExternalAccountStatusLabel from '@/components/ExternalAccountStatusLabel';
import {
  ExternalAccountDetails,
  ExternalAccountType,
} from '@/types/externalAccounts';
import { buildAddress } from '@/utils/address';
import { formatDateTimeFromUTC } from '@/utils/date';

type ExternalAccountDetailsCardsProps = {
  externalAccountDetails: ExternalAccountDetails;
};

export default function ExternalAccountDetailsCards({
  externalAccountDetails,
}: ExternalAccountDetailsCardsProps) {
  const {
    status,
    requestedAt,
    declinedAt,
    verifiedAt,
    submittedAt,
    consentType,
    eSignProvider,
    verificationType,
    identityMatch,
    bankName,
    accountType,
    accountNumberLast4,
    routingNumber,
    bankAddress,
    supportsAchTransfers,
    supportsWireTransfers,
    owner,
    ownerAddress,
  } = externalAccountDetails;

  const paymentTypes =
    supportsAchTransfers && supportsWireTransfers
      ? 'ACH & Wire'
      : supportsAchTransfers && !supportsWireTransfers
        ? 'ACH'
        : 'Wire';
  const externalAccountTypeLabel =
    accountType === ExternalAccountType.CHECKING
      ? 'Checking'
      : accountType === ExternalAccountType.BROKERAGE
        ? 'Brokerage'
        : accountType === ExternalAccountType.SAVINGS
          ? 'Savings'
          : 'Other';

  return (
    <>
      <SVSummaryCard
        label="Submission Details"
        items={[
          {
            label: 'Status',
            customContent: <ExternalAccountStatusLabel status={status} />,
          },
          { label: 'Submitted Date', text: formatDateTimeFromUTC(submittedAt) },
          { label: 'Requested Date', text: formatDateTimeFromUTC(requestedAt) },
          { label: 'Declined Date', text: formatDateTimeFromUTC(declinedAt) },
          { label: 'Verified Date', text: formatDateTimeFromUTC(verifiedAt) },
          { label: 'Verification Type', text: verificationType },
          { label: 'Consent Type', text: consentType },
          { label: 'Esign Provider', text: eSignProvider },
          { label: 'Payment Types', text: paymentTypes },
        ]}
      />
      {identityMatch && (
        <SVSummaryCard
          label="Identity Verification"
          items={[
            { label: 'Name Score', text: identityMatch.legalNameScore },
            { label: 'Address Score', text: identityMatch.addressScore },
            {
              label: 'Phone Number Score',
              text: identityMatch.phoneNumberScore,
            },
            { label: 'Email Score', text: identityMatch.emailAddressScore },
          ]}
        />
      )}
      <SVSummaryCard
        label="Bank Info"
        items={[
          {
            label: 'Bank Name',
            text: bankName,
          },
          {
            label: 'Account Type',
            text: externalAccountTypeLabel,
          },
          {
            label: 'Account Number',
            text: `*****${accountNumberLast4}`,
          },
          {
            label: 'Routing Number',
            text: routingNumber,
          },
          {
            label: 'Bank Address',
            text: buildAddress({
              line1: bankAddress?.line1 || '',
              line2: bankAddress?.line2 || '',
              city: bankAddress?.city || '',
              country: bankAddress?.country || '',
              postalCode: bankAddress?.postalCode || '',
              state: bankAddress?.state || '',
            }),
          },
        ]}
      />
      <SVSummaryCard
        label="Owner Info"
        items={[
          {
            label: 'Name on Account',
            text: owner.fullName,
          },
          {
            label: 'Owner Address',
            text: buildAddress({
              line1: ownerAddress.line1,
              line2: ownerAddress.line2,
              city: ownerAddress.city,
              country: ownerAddress.country,
              postalCode: ownerAddress.postalCode,
              state: ownerAddress.state || '',
            }),
          },
        ]}
      />
    </>
  );
}
